<template>
  <RevSkeleton
    v-if="!isClientSide"
    class="rounded-lg hidden lg:block"
    height="234px"
    shape="rectangle"
    width="478px"
  />

  <RevSkeleton
    v-if="!isClientSide"
    class="rounded-lg lg:hidden"
    height="200px"
    shape="rectangle"
    width="100%"
  />

  <div
    v-else
    class="bg-surface-default-low rounded-lg shrink-0 overflow-hidden lg:w-fit"
    :class="{
      'p-24 lg:px-32 lg:pb-0 lg:pt-32': consentGiven,
      'p-24 lg:h-[220px] lg:py-0 lg:pl-32 lg:pr-0': !consentGiven,
    }"
    data-test="app-store-download-banner-medium"
  >
    <div
      class="flex"
      :class="{
        'h-full lg:min-h-[152px] lg:justify-between': !consentGiven,
        'justify-center gap-24': consentGiven,
      }"
    >
      <RevIllustration
        v-if="consentGiven && qrCodePath"
        :alt="i18n(translations.downloadAppQrCodeImage)"
        class="hidden self-end lg:block"
        data-test="app-store-download-banner-medium-qr-code"
        :height="212"
        :src="qrCodePath"
        :width="128"
      />

      <div
        class="flex flex-col justify-center"
        :class="{
          'w-full lg:justify-center': !consentGiven,
          'lg:justify-start': consentGiven,
        }"
      >
        <div class="flex justify-center">
          <RevIllustration
            :alt="i18n(translations.downloadAppBrandImage)"
            class="mr-10 !size-40 lg:hidden"
            :height="40"
            src="/img/lead-collection/appDownload/bm-logo-dot-black.svg"
            :width="40"
          />

          <div class="flex-col">
            <p v-if="title" class="body-1-bold self-center lg:heading-2">
              {{ i18n(title) }}
            </p>

            <p v-if="subtitle" class="body-1">
              {{ i18n(subtitle) }}
            </p>
          </div>
        </div>

        <div class="flex flex-col-reverse items-center lg:items-start">
          <div class="mt-8 flex items-center">
            <RevRating v-if="rating" :score="rating" size="small" />

            <p class="caption">
              <span class="px-8">•</span>{{ i18n(translations.ratingMessage) }}
            </p>
          </div>

          <AppStoreButtons
            :apple-store-tracking-name="`${routeName}_new_app_store`"
            :apple-store-url="desktopTrackedUrl"
            class="mt-16"
            :class="{
              'hidden lg:flex': desktopTrackedUrl && consentGiven,
            }"
            :play-store-tracking-name="`${routeName}_new_google_play`"
            :play-store-url="desktopTrackedUrl"
            :show-in-all-countries="true"
            trackingZone="app_download_new_notification"
          />

          <RevButton
            v-if="mobileTrackedUrl && consentGiven"
            class="mt-16 lg:hidden"
            data-test="app-store-download-banner-medium-single-button"
            full-width="always"
            target="_blank"
            :to="mobileTrackedUrl"
            :tracking="{
              name: `${routeName}_new_tracked_store`,
              url: mobileTrackedUrl,
              zone: 'app_download_new_notification',
            }"
            variant="primary"
          >
            {{ i18n(translations.downloadApp) }}
          </RevButton>
        </div>
      </div>

      <RevIllustration
        v-if="!consentGiven"
        :alt="i18n(translations.downloadAppGenericPhoneImage)"
        class="hidden lg:block"
        :class="{ '-mr-[55px]': !consentGiven }"
        data-test="app-store-download-banner-medium-generic-phone"
        :height="152"
        src="/img/lead-collection/appDownload/app-download-generic.png"
        :width="220"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, onMounted, ref } from 'vue'

import AppStoreButtons from '@backmarket/nuxt-layer-base/components/AppStoreButtons/AppStoreButtons.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevIllustration } from '@ds/components/Illustration'
import { RevRating } from '@ds/components/Rating'
import { RevSkeleton } from '@ds/components/Skeleton'

import translations from '../AppStoreDownloadBanner.translations'
import type { BannerVariantProps } from '../AppStoreDownloadBanner.types'

const i18n = useI18n()
const route = useRoute()

const {
  consentGiven = false,
  desktopTrackedUrl = '',
  mobileTrackedUrl = '',
} = defineProps<BannerVariantProps>()

const routeName = computed(() => route.name?.toString() || '')

const isClientSide = ref(false)

onMounted(() => {
  isClientSide.value = true
})
</script>
